import { produce } from 'immer';
import _ from 'lodash';
import { isLosslessNumber } from 'lossless-json';

export const deepClone = (obj: {
  [key: string]: unknown;
}): {
  [key: string]: unknown;
} => {
  if (!obj) return {};

  return JSON.parse(JSON.stringify(obj));
};

export const removeLosslessObject = (obj: { [key: string]: any }) => {
  if (isLosslessNumber(obj)) {
    return obj.valueOf();
  }

  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i += 1) {
      obj[i] = removeLosslessObject(obj[i]);
    }
    return obj;
  }

  Object.keys(obj).forEach((key) => {
    obj[key] = removeLosslessObject(obj[key]);
  });

  return obj;
};

export const isEqual = (obj1: any, obj2: any): boolean => {
  const diff = calculateDifference(obj1, obj2);

  if (Object.keys(diff).length === 0) return true;

  function findTrueValue(obj: any) {
    if (typeof obj !== 'object' && obj != null && !Array.isArray(obj)) {
      return obj;
    }

    if (Array.isArray(obj)) {
      if (
        typeof obj[0] !== 'object' &&
        obj[0] != null &&
        !Array.isArray(obj[0])
      ) {
        const filterTrueValues = obj.filter((item: any) => item === true);

        if (filterTrueValues?.length > 0) return true;

        return false;
      }
    }

    if (obj) {
      const keys = Object.keys(obj);

      for (let i = 0; i < keys.length; i += 1) {
        const res = findTrueValue(obj[keys[i]]);

        if (res) {
          return true;
          break;
        }
      }

      return false;
    }
  }

  const keys = Object.keys(diff);

  for (let i = 0; i < keys.length; i += 1) {
    const res = findTrueValue(diff[keys[i]]);

    if (res) {
      return false;
      break;
    }
  }

  return true;
};

export const calculateDifference = (
  base: any,
  object: any,
): { [key: string]: any } => {
  function changes(inBase: any, inObject: any) {
    if (!inBase && inObject) {
      if (Array.isArray(inObject)) {
        return inObject.length > 0;
      }

      return true;
    }

    if (inBase && !inObject) {
      if (Array.isArray(inBase)) {
        return inBase.length > 0;
      }

      return true;
    }

    if (
      typeof inBase !== 'object' &&
      inBase != null &&
      !Array.isArray(inBase)
    ) {
      return inBase !== inObject;
    }

    if (isLosslessNumber(inBase)) {
      return !isLosslessNumber(inObject) || inBase.value !== inObject.value;
    }

    if (Array.isArray(inBase)) {
      if (!Array.isArray(inObject)) return true;

      if (
        typeof inBase[0] !== 'object' &&
        inBase[0] != null &&
        !Array.isArray(inBase[0])
      ) {
        const result = [];
        inBase.forEach((val) => {
          result.push(!inObject.includes(val));
        });

        return _.difference(inBase, inObject)?.length === inBase?.length
          ? true
          : result;
      }

      // if is array of object
      const result = [];
      for (let i = 0; i < inBase.length; i += 1) {
        result.push(changes(inBase[i], inObject[i]));
      }
    }

    const diff = {};

    if (inBase) {
      Object.keys(inBase).forEach((baseKey) => {
        if (
          !inObject ||
          !Object.prototype.hasOwnProperty.call(inObject, baseKey)
        ) {
          diff[baseKey] = true;
          return;
        }

        const res = changes(inBase?.[baseKey], inObject?.[baseKey]);

        if (typeof res !== 'object' && res != null && !Array.isArray(res)) {
          diff[baseKey] = res;

          return;
        }

        if (Object.keys(res)?.length > 0) diff[baseKey] = res;
      });
    }

    return diff;
  }

  return changes(base, object);
};

export const deepMerge = (
  base: { [key: string]: any },
  inObject: { [key: string]: any },
) => {
  const nextBase = produce(base, (draft) => {
    function replace(inBase: any, inObject: any) {
      if (
        !inObject ||
        typeof inObject !== 'object' ||
        Array.isArray(inObject)
      ) {
        return;
      }

      // Process existing keys in base
      Object.keys(inBase).forEach((baseKey) => {
        if (!Object.prototype.hasOwnProperty.call(inObject, baseKey)) {
          return;
        }

        const baseValue = inBase[baseKey];
        const objectValue = inObject[baseKey];

        // Handle primitive values
        if (
          typeof baseValue !== 'object' ||
          baseValue == null ||
          Array.isArray(baseValue)
        ) {
          inBase[baseKey] = objectValue;
          return;
        }

        // Handle nested objects
        if (
          typeof objectValue === 'object' &&
          objectValue != null &&
          !Array.isArray(objectValue)
        ) {
          replace(inBase[baseKey], objectValue);
        } else {
          inBase[baseKey] = objectValue;
        }
      });

      // Add new keys from inObject that don't exist in inBase
      Object.keys(inObject).forEach((objectKey) => {
        if (!Object.prototype.hasOwnProperty.call(inBase, objectKey)) {
          inBase[objectKey] = inObject[objectKey];
        }
      });
    }

    replace(draft, inObject);
  });

  return nextBase;
};
